import React from 'react'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components/macro'
import { Button } from 'components'

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
`

const SignerList = styled.ul`
	margin: 0 0 1rem 1.2rem;
	padding: 0;
`

const StatusBox = styled.div`
	background: ${props => props.theme.softColor};
	border-left: 4px solid ${props => props.theme.accentColor};
	border-radius: 0 6px 6px 0;
	padding: 16px 20px;
	margin-bottom: 1.25rem;
`

const SUPPORT_EMAIL = 'yrityssaneeraus@yrityskorjaamo.fi'

// Allekirjoituskutsujen luonti Visma Signiin kestää yleensä hetken. Jos tila on
// yhä PAID tämän jälkeen, kerrotaan asiakkaalle, miten toimia.
const PAID_WAIT_POLLS = 6

const Signing = ({ match }) => {
	const applicationId = match.params.applicationId
	const history = useHistory()
	const [application, setApplication] = React.useState(null)
	const [demoMode, setDemoMode] = React.useState(false)
	const [simulatePayments, setSimulatePayments] = React.useState(false)
	const [loading, setLoading] = React.useState(true)
	const [completing, setCompleting] = React.useState(false)
	const [error, setError] = React.useState(null)
	const [paidPolls, setPaidPolls] = React.useState(0)

	const signers = React.useMemo(() => {
		if (!application) {
			return []
		}
		const list = []
		if (application.authorizer?.email) {
			list.push({
				name: application.authorizer.name || 'Valtuutettu',
				email: application.authorizer.email,
				role: 'Hakemus',
			})
		}
		;(application.stakeHolders || []).forEach(sh => {
			if (sh.email) {
				list.push({
					name: sh.name || 'Osakkeenomistaja',
					email: sh.email,
					role: 'Päätöspöytäkirja',
				})
			}
		})
		return list
	}, [application])

	React.useEffect(() => {
		let cancelled = false
		let pollTimer

		const ensureSigningStarted = async (app, isSimulated) => {
			if (
				isSimulated &&
				(app.status === 'PAID' ||
					(app.status === 'SIGNATURE_PENDING' &&
						(!app.signatures || app.signatures.length === 0)))
			) {
				const payRes = await axios.post(
					`/api/applications/${applicationId}/demo-pay`
				)
				return payRes.data
			}
			return app
		}

		const load = async () => {
			try {
				const configRes = await axios.get('/api/site-config')
				const isDemo = !!configRes.data.demoMode
				const isSimulated = !!configRes.data.simulatePayments
				if (!cancelled) {
					setDemoMode(isDemo)
					setSimulatePayments(isSimulated)
				}

				const appRes = await axios.get(`/api/applications/${applicationId}`)
				let app = await ensureSigningStarted(appRes.data, isSimulated)

				if (!cancelled) {
					setApplication(app)
					setLoading(false)
				}

				if (app.status === 'FINISHED') {
					history.replace(`/form/${applicationId}/success`)
					return
				}

				// Allekirjoitussivu kuuluu vasta maksun jälkeen.
				if (app.status !== 'PAID' && app.status !== 'SIGNATURE_PENDING') {
					history.replace(`/form/${applicationId}`)
					return
				}

				pollTimer = setInterval(async () => {
					try {
						const res = await axios.get(
							`/api/applications/${applicationId}`
						)
						if (cancelled) {
							return
						}
						setApplication(res.data)
						if (res.data.status === 'PAID') {
							setPaidPolls(n => n + 1)
						}
						if (res.data.status === 'FINISHED') {
							clearInterval(pollTimer)
							history.replace(`/form/${applicationId}/success`)
						}
					} catch (pollErr) {
						// keep polling
					}
				}, 10000)
			} catch (err) {
				if (!cancelled) {
					setError(
						`Hakemuksen tietojen lataus epäonnistui. Yritä hetken päästä uudelleen tai ota yhteyttä: ${SUPPORT_EMAIL}`
					)
					setLoading(false)
				}
			}
		}

		load()

		return () => {
			cancelled = true
			if (pollTimer) {
				clearInterval(pollTimer)
			}
		}
	}, [applicationId, history])

	const handleCompleteSimulatedSigning = async () => {
		setCompleting(true)
		setError(null)
		try {
			const res = await axios.post(
				`/api/applications/${applicationId}/demo-signing`
			)
			setApplication(res.data)
			if (res.data.status === 'FINISHED') {
				history.replace(`/form/${applicationId}/success`)
			}
		} catch (err) {
			setError('Allekirjoitusten simulointi epäonnistui')
			setCompleting(false)
		}
	}

	if (loading) {
		return (
			<Wrapper>
				<h1>Allekirjoitukset</h1>
				<p>
					{simulatePayments
						? 'Valmistellaan allekirjoitusprosessia...'
						: 'Valmistellaan allekirjoitusprosessia Visma Signissa...'}
				</p>
			</Wrapper>
		)
	}

	if (error && !application) {
		return (
			<Wrapper>
				<h1>Virhe</h1>
				<p>{error}</p>
			</Wrapper>
		)
	}

	const preparing = application?.status === 'PAID'

	if (preparing && !simulatePayments) {
		return (
			<Wrapper>
				<h1>Allekirjoitukset</h1>
				<StatusBox>
					<strong>Maksu vastaanotettu</strong>
					<p style={{ margin: '0.5rem 0 0' }}>
						Allekirjoituskutsuja valmistellaan Visma Signissa. Sivu päivittyy
						automaattisesti.
					</p>
				</StatusBox>
				{paidPolls >= PAID_WAIT_POLLS && (
					<p>
						Allekirjoituskutsujen lähetys viivästyy. Jos kutsu ei saavu
						sähköpostiisi 15 minuutin kuluessa, ota yhteyttä:{' '}
						<a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
					</p>
				)}
			</Wrapper>
		)
	}

	return (
		<Wrapper>
			<h1>Allekirjoitukset</h1>
			<StatusBox>
				<strong>
					{simulatePayments
						? 'Odottaa allekirjoituksia (simuloitu)'
						: 'Odottaa allekirjoituksia Visma Signissa'}
				</strong>
				<p style={{ margin: '0.5rem 0 0' }}>
					{simulatePayments
						? 'Testiympäristössä Visma Sign ohitetaan. Voit vahvistaa allekirjoitukset alla olevalla napilla.'
						: demoMode
						? 'Allekirjoituskutsut lähetetään allekirjoittajien sähköpostiin Visma Signin kautta. Tarkista postilaatikko ja allekirjoita molemmat dokumentit.'
						: 'Allekirjoituskutsut on lähetetty Visma Sign -palvelun kautta. Siirryt automaattisesti eteenpäin, kun kaikki ovat allekirjoittaneet.'}
				</p>
			</StatusBox>
			<p>Allekirjoittajat:</p>
			<SignerList>
				{signers.map(signer => (
					<li key={signer.email + signer.role}>
						<strong>{signer.name}</strong> ({signer.email}) – {signer.role}
					</li>
				))}
			</SignerList>
			{error && <p style={{ color: 'crimson' }}>{error}</p>}
			{simulatePayments ? (
				<Button
					variant="filled"
					disabled={completing}
					onClick={handleCompleteSimulatedSigning}
				>
					{completing
						? 'Käsitellään...'
						: 'Simuloi allekirjoitukset valmiiksi'}
				</Button>
			) : (
				<p>
					Kun kaikki ovat allekirjoittaneet Visma Signissa, sivu päivittyy
					automaattisesti.
				</p>
			)}
		</Wrapper>
	)
}

export default Signing
